import React from 'react';
import { Settings, Power, RefreshCw, Mic, Activity, Cpu, Thermometer, Zap, Server, Network, Radio } from 'lucide-react';
import { AVSlider } from './ui/AVSlider';
import { AVButton } from './ui/AVButton';
import { MicChannel } from '../types/crestron';
interface AdvancedPageProps {
  mics: MicChannel[];
  autoDucking: boolean;
  onMicLevelChange: (id: string, level: number) => void;
  onMicMuteToggle: (id: string) => void;
  onToggleAutoDucking: () => void;
}
export function AdvancedPage({
  mics,
  autoDucking,
  onMicLevelChange,
  onMicMuteToggle,
  onToggleAutoDucking
}: AdvancedPageProps) {
  const deviceHealth = [{
    name: 'Crestron CP4',
    status: 'online',
    temp: 42,
    load: 23,
    icon: Cpu,
    color: 'green'
  }, {
    name: 'Bose DSP-1280C',
    status: 'online',
    temp: 38,
    load: 45,
    icon: Server,
    color: 'green'
  }, {
    name: 'WyreStorm NHD',
    status: 'online',
    temp: 40,
    load: 67,
    icon: Network,
    color: 'green'
  }, {
    name: 'Shure MXW',
    status: 'online',
    temp: 35,
    load: 12,
    icon: Radio,
    color: 'green'
  }];
  return <div className="h-full p-4 flex flex-col gap-4" data-id="element-2">
      {/* Top: Mic Mixer */}
      <div className="flex-1 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 shadow-xl" data-id="element-3">
        <div className="flex items-center justify-between mb-4" data-id="element-4">
          <div data-id="element-5">
            <h2 className="text-base font-bold text-white flex items-center gap-2 mb-0.5" data-id="element-6">
              <div className="p-1.5 bg-purple-600/20 rounded-lg" data-id="element-7">
                <Mic className="text-purple-400" size={18} strokeWidth={2.5} data-id="element-8" />
              </div>
              Individual Microphone Control
            </h2>
            <p className="text-[10px] text-gray-500" data-id="element-9">Fine-tune each channel</p>
          </div>
          <div className="flex items-center gap-2 px-2.5 py-1 bg-green-900/30 rounded-lg border border-green-800" data-id="element-10">
            <Activity size={12} className="text-green-400" data-id="element-11" />
            <span className="text-[10px] font-bold text-green-400" data-id="element-12">
              DSP ONLINE
            </span>
          </div>
        </div>

        <div className="flex gap-3 h-[220px]" data-id="element-13">
          {mics.map(mic => <div key={mic.id} className="flex-1 bg-neutral-900/80 rounded-lg p-3 border border-neutral-800 flex flex-col items-center gap-2 hover:border-neutral-700 transition-colors" data-id="element-14">
              <div className={`p-1.5 rounded-lg ${mic.muted ? 'bg-red-900/30' : 'bg-green-900/30'}`} data-id="element-15">
                <Mic size={16} className={mic.muted ? 'text-red-400' : 'text-green-400'} strokeWidth={2} data-id="element-16" />
              </div>

              <div className="text-[10px] font-bold text-gray-300 truncate w-full text-center uppercase tracking-wider" data-id="element-17">
                {mic.name}
              </div>

              <div className="flex-1 w-full flex justify-center py-1" data-id="element-18">
                <AVSlider value={mic.level} onChange={v => onMicLevelChange(mic.id, v)} height="h-full" color="blue" showValue={false} data-id="element-19" />
              </div>

              <div className="text-[10px] font-mono font-bold text-gray-400 tabular-nums" data-id="element-20">
                {mic.level}%
              </div>

              <button onClick={() => onMicMuteToggle(mic.id)} className={`
                  w-full py-1.5 rounded-lg font-bold text-[10px] uppercase transition-all
                  ${mic.muted ? 'bg-red-600 text-white shadow-[0_0_12px_rgba(220,38,38,0.4)] border-2 border-red-400' : 'bg-green-600 text-white shadow-[0_0_12px_rgba(34,197,94,0.4)] border-2 border-green-400'}
                `} data-id="element-21">
                {mic.muted ? 'MUTED' : 'ACTIVE'}
              </button>
            </div>)}
        </div>
      </div>

      {/* Bottom Grid: System Tools & Health */}
      <div className="h-[220px] grid grid-cols-12 gap-4" data-id="element-22">
        {/* Audio Processing */}
        <div className="col-span-3 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 flex flex-col justify-between shadow-xl" data-id="element-23">
          <div data-id="element-24">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-25">
              <Zap size={16} className="text-blue-400" data-id="element-26" />
              <h3 className="text-sm font-bold text-white" data-id="element-27">Audio Processing</h3>
            </div>
            <p className="text-[10px] text-gray-500 mb-3" data-id="element-28">BGM behavior</p>
          </div>
          <AVButton variant={autoDucking ? 'active' : 'default'} label="AUTO-DUCK" subLabel={autoDucking ? 'BGM lowers' : 'BGM constant'} onClick={onToggleAutoDucking} size="sm" data-id="element-29" />
        </div>

        {/* Device Health Monitor */}
        <div className="col-span-6 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 shadow-xl" data-id="element-30">
          <div className="flex items-center gap-1.5 mb-3" data-id="element-31">
            <Activity size={16} className="text-green-400" strokeWidth={2.5} data-id="element-32" />
            <h3 className="text-sm font-bold text-white" data-id="element-33">System Health</h3>
          </div>

          <div className="space-y-2" data-id="element-34">
            {deviceHealth.map(device => {
            const DeviceIcon = device.icon;
            return <div key={device.name} className="flex items-center justify-between p-2 bg-neutral-900/50 rounded-lg border border-neutral-800" data-id="element-35">
                  <div className="flex items-center gap-2" data-id="element-36">
                    <div className="p-1.5 bg-green-900/30 rounded-lg" data-id="element-37">
                      <DeviceIcon size={14} className="text-green-400" data-id="element-38" />
                    </div>
                    <div data-id="element-39">
                      <div className="text-xs font-bold text-white leading-none" data-id="element-40">
                        {device.name}
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5" data-id="element-41">
                        <span className="text-green-400 font-bold uppercase" data-id="element-42">
                          {device.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 text-[10px]" data-id="element-43">
                    <div className="flex items-center gap-1" data-id="element-44">
                      <Thermometer size={12} className="text-blue-400" data-id="element-45" />
                      <span className="text-gray-400" data-id="element-46">{device.temp}°C</span>
                    </div>
                    <div className="flex items-center gap-1" data-id="element-47">
                      <Cpu size={12} className="text-purple-400" data-id="element-48" />
                      <span className="text-gray-400" data-id="element-49">{device.load}%</span>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* System Power Controls */}
        <div className="col-span-3 bg-gradient-to-br from-neutral-800 to-neutral-900 rounded-xl p-4 border border-neutral-700 flex flex-col justify-between shadow-xl" data-id="element-50">
          <div data-id="element-51">
            <div className="flex items-center gap-1.5 mb-1" data-id="element-52">
              <Power size={16} className="text-red-400" data-id="element-53" />
              <h3 className="text-sm font-bold text-white" data-id="element-54">System Power</h3>
            </div>
            <p className="text-[10px] text-gray-500 mb-3" data-id="element-55">
              Control & diagnostics
            </p>
          </div>
          <div className="flex flex-col gap-2" data-id="element-56">
            <AVButton variant="default" icon={RefreshCw} label="REBOOT" size="sm" fullWidth data-id="element-57" />
            <AVButton variant="danger" icon={Power} label="SHUTDOWN" size="sm" fullWidth data-id="element-58" />
          </div>
        </div>
      </div>
    </div>;
}